import { Controller } from '@hotwired/stimulus';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static targets = ['sheet'];

  connect() {
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('click', this.handleTriggerClick);

    // A sheet can arrive already open (`open: true`), e.g. re-rendered by a
    // turbo_stream after a failed form submission. Acquire the same state
    // open() would have set up.
    if (!this.element.hidden) this.acquireOpenState();
  }

  disconnect() {
    document.removeEventListener('click', this.handleTriggerClick);
    this.releaseOpenState();
  }

  open() {
    if (!this.element.hidden) return;

    this.previousFocus = document.activeElement;
    this.element.classList.remove('ui-sheet-overlay--closing');
    this.element.hidden = false;
    this.acquireOpenState();

    this.dispatch('open');
  }

  requestClose() {
    if (this.element.hidden) return;
    this.element.classList.add('ui-sheet-overlay--closing');
  }

  animationEnd(event) {
    if (event.target !== this.sheetTarget) return;
    if (!this.element.classList.contains('ui-sheet-overlay--closing')) return;

    this.element.classList.remove('ui-sheet-overlay--closing');
    this.element.hidden = true;
    this.releaseOpenState();

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.dispatch('close');
  }

  backdropClick(event) {
    if (event.target === this.element) {
      this.requestClose();
    }
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest(`[data-sheet-trigger="${this.element.id}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  // Keep Tab cycling within the sheet while it is open (mirrors the React
  // Sheet's focus trap).
  trapFocus(event) {
    if (!this.hasSheetTarget) return;

    const focusable = this.sheetTarget.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  acquireOpenState() {
    document.body.style.overflow = 'hidden';
    this.scrollLocked = true;
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      if (this.hasSheetTarget) this.sheetTarget.focus();
    });
  }

  releaseOpenState() {
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.scrollLocked) {
      document.body.style.overflow = '';
      this.scrollLocked = false;
    }
  }
}
